import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

export const BirthCityCountryModal = ({ show, onClose, onSelect }) => {
  const token = Cookies.get("auth_token");
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const debounceRef = useRef(null);

  const resetState = () => {
    setSearchText("");
    setResults([]);
    setLoading(false);
    setError("");
    setSelectedItem(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const fetchSuggestions = async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setError("Required. Typed search text; min 3 characters after trim");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("letter", trimmed);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/location/birth_city_country`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();
      const nextResults = Array.isArray(data.data) ? data.data : [];

      if (data.status === "1") {
        setResults(nextResults);
        if (nextResults.length === 0) {
          setError("No results found");
        }
      } else {
        setResults([]);
        setError(data.message || "No results found");
      }
    } catch (err) {
      setResults([]);
      setError(err.message || "Search failed");
      toast.dismiss();
      toast.error(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(searchText);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText, show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-[520px] rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Select Birth City and Country</h3>
            <p className="text-xs text-gray-500">Search by at least 3 characters</p>
          </div>
          <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        <div className="relative mb-3">
          <img src="../chatbox/magnifer.svg" alt="search" className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 opacity-70" />
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Type city or country"
            className="w-full rounded-[14px] border border-[#E6E2FF] bg-white py-3 pl-12 pr-4 text-gray-800 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15"
          />
        </div>

        {error && <p className="mb-3 text-sm text-red-500">{error}</p>}

        <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 rounded-xl border border-gray-100">
          {loading ? (
            <div className="p-4 text-sm text-gray-500">Searching...</div>
          ) : results.length > 0 ? (
            results.map((item) => {
              const label = item?.birth_city_country || item?.description || item?.main_text || "";
              const subtitle = item?.secondary_text || item?.main_text || "";
              const isSelected = selectedItem?.place_id === item?.place_id;

              return (
                <button
                  key={item?.place_id || label}
                  type="button"
                  onClick={() => setSelectedItem(item)}
                  className={`flex w-full items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left transition last:border-b-0 ${
                    isSelected ? "bg-[#F3EEFF]" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-800">{label}</p>
                    <p className="truncate text-xs text-gray-500">{subtitle}</p>
                  </div>
                  <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${isSelected ? "border-[#7C4DFF] bg-[#7C4DFF]" : "border-gray-300"}`}>
                    {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-white" />}
                  </div>
                </button>
              );
            })
          ) : (
           <></> // <div className="p-4 text-sm text-gray-500">Type at least 3 characters to search.</div>
          )}
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 rounded-full border-2 border-gray-300 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (!selectedItem) {
                toast.error("Please select a city");
                return;
              }
              onSelect(selectedItem);
              resetState();
              onClose();
            }}
            disabled={!selectedItem}
            className="submit-button flex-1 rounded-full py-2.5 font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
};